import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useForm } from '../../../../lib/components';
import { useStore } from '../../../../hooks/useStore';
import { PositionDataView } from './view';
import { PositionDataForm } from './form';
import { EmployeePositionData } from '@pxtra-client-lib-legacy/gen';
import { ProfileViewEditBox } from '../shared/profileViewEditBox';
import * as Yup from 'yup';
import { useGetEmployeePositionData } from '../../../../queries/employee';
import {
  useUpdateEmployeeExtendedData,
  useUpdateEmployeePositionData
} from '../../../../queries/employee/mutations';
import { enqueueSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';

export const EmployeeProfilPositionData = observer(() => {
  const { employeeStore, companyStore } = useStore();
  const { employeeId } = useParams();
  const { t } = useTranslation();
  const form = useForm();
  const queryClient = useQueryClient();

  const { data: positionData } = useGetEmployeePositionData(
    employeeStore.currentEmployee?.id ?? ''
  );
  const { mutate: updatePositionData } = useUpdateEmployeePositionData({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee'] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      enqueueSnackbar(t('common:savingSuccess'), {
        variant: 'success'
      });
    },
    onError: () => {
      enqueueSnackbar(t('template:SAVING_ERROR'), {
        variant: 'error'
      });
    }
  });
  const { mutate: updateExtendedData } = useUpdateEmployeeExtendedData({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee'] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    }
  });

  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  const [extendedPositionData, setExtendedPositionData] = useState<EmployeePositionData>();

  const renderView = observer(() => <PositionDataView data={extendedPositionData} />);

  const renderEdit = () => {
    return (
      <PositionDataForm
        initialValues={{
          position_name: positionData?.position_name ?? '',
          employee_number: positionData?.employee_number ?? '',
          entry_date: positionData?.entry_date ?? '',
          exit_date: positionData?.exit_date ?? '',
          note: positionData?.note ?? '',
          department: employeeStore.currentEmployee?.department ?? '',
          location: employeeStore.currentEmployee?.location ?? '',
          cost_center: employeeStore.currentEmployee?.cost_center ?? '',
          working_days_per_week: positionData?.working_days_per_week ?? '',
          working_hours_per_week: positionData?.working_hours_per_week ?? '',
          missing_fields: positionData?.missing_fields
        }}
        validationSchema={{
          employee_number: Yup.string().required(t('form:FIELD_IS_REQUIRED')),
          working_days_per_week: Yup.number()
            .typeError(t('form:INVALID_NUMBER'))
            .min(1, t('form:MUST_BE_BETWEEN', { min: 1, max: 7 }))
            .max(7, t('form:MUST_BE_BETWEEN', { min: 1, max: 7 })),
          working_hours_per_week: Yup.number()
            .typeError(t('form:INVALID_NUMBER'))
            .min(1, t('form:MUST_BE_BETWEEN', { min: 1, max: 48 }))
            .max(48, t('form:MUST_BE_BETWEEN', { min: 1, max: 48 }))
        }}
        formRef={form.formRef}
        onStateChanged={setCanSubmit}
        onSubmit={async (values) => {
          try {
            if (employeeId) {
              updatePositionData({
                employeeId,
                requestBody: {
                  ...values,
                  entry_date: values.entry_date || null,
                  exit_date: values.exit_date || null,
                  working_days_per_week: values.working_days_per_week || null,
                  working_hours_per_week: values.working_hours_per_week || null
                }
              });
              updateExtendedData({
                employeeId,
                requestBody: {
                  department: values.department,
                  location: values.location,
                  cost_center: values.cost_center
                }
              });
            }
          } catch (error) {
            console.error(error);
          }
        }}
      />
    );
  };

  useEffect(() => {
    const fn = async () => {
      await companyStore.fetchLocations();
      await companyStore.fetchDepartments();
      await companyStore.fetchCostCenters();

      positionData &&
        setExtendedPositionData({
          ...positionData
        });
    };
    fn();
  }, [positionData]);

  return (
    <ProfileViewEditBox
      title={t('employee:POSITION_DATA')}
      view={renderView}
      edit={renderEdit}
      canEdit
      canSubmit={canSubmit}
      onSubmit={form.handleSubmit}
      onCancel={form.handleReset}
    />
  );
});
